/**
 * Suggestion Engine
 *
 * Generates filter suggestions based on user input text
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterSuggestion,
  SuggestionOptions,
} from '../types';

/**
 * Default suggestion options
 */
const DEFAULT_OPTIONS: Required<SuggestionOptions> = {
  maxSuggestions: 10,
  minQueryLength: 0, // Allow empty query to show filter categories
  includeFilters: [],
  excludeFilters: [],
};

/**
 * Get filter category suggestions (shown when query is empty)
 * These are top-level filter options user can select from
 */
export function getFilterCategories(
  definitions: Record<string, FilterDefinition>,
  options: SuggestionOptions = {}
): FilterSuggestion[] {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Determine which filters to use
  let filterIds = Object.keys(definitions);

  if (opts.includeFilters && opts.includeFilters.length > 0) {
    filterIds = filterIds.filter((id) => opts.includeFilters!.includes(id));
  }

  if (opts.excludeFilters && opts.excludeFilters.length > 0) {
    filterIds = filterIds.filter((id) => !opts.excludeFilters!.includes(id));
  }

  // Create category suggestions for each filter type
  const categories: FilterSuggestion[] = filterIds.map((filterId, index) => {
    const definition = definitions[filterId];
    return {
      filterId,
      label: definition.label,
      description: `Filter by ${definition.label.toLowerCase()}`,
      prefilledOperator: definition.defaultOperator,
      prefilledValue: '',
      needsConfiguration: true, // Always open config for category selection
      score: 100 - index, // Maintain order
      isCategory: true, // Mark as category for special handling
    } as FilterSuggestion & { isCategory?: boolean };
  });

  return categories.slice(0, opts.maxSuggestions);
}

/**
 * Generate filter suggestions based on user query
 *
 * @param query - User's search input
 * @param definitions - Available filter definitions
 * @param nodes - All nodes for context
 * @param options - Configuration options
 * @returns Array of suggestions sorted by relevance
 */
export function getSuggestions(
  query: string,
  definitions: Record<string, FilterDefinition>,
  nodes: GraphDataNode[],
  options: SuggestionOptions = {}
): FilterSuggestion[] {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const trimmedQuery = query.trim();

  // If query is empty, return filter categories
  if (trimmedQuery.length === 0) {
    return getFilterCategories(definitions, options);
  }

  // Check minimum query length for actual searches
  if (trimmedQuery.length < opts.minQueryLength) {
    return [];
  }

  // Determine which filters to use
  let filterIds = Object.keys(definitions);

  if (opts.includeFilters.length > 0) {
    filterIds = filterIds.filter((id) => opts.includeFilters.includes(id));
  }

  if (opts.excludeFilters.length > 0) {
    filterIds = filterIds.filter((id) => !opts.excludeFilters.includes(id));
  }

  // Collect suggestions from all filter definitions
  const allSuggestions: FilterSuggestion[] = [];

  for (const filterId of filterIds) {
    const definition = definitions[filterId];
    if (!definition) continue;

    try {
      const suggestions = definition.suggestMatch(trimmedQuery, nodes);
      allSuggestions.push(...suggestions);
    } catch (error) {
      console.warn(`Error generating suggestions for filter "${filterId}":`, error);
    }
  }

  // Sort by score (descending) and deduplicate
  const sortedSuggestions = allSuggestions.sort((a, b) => b.score - a.score);

  // Deduplicate by label (keep highest scored)
  const seen = new Set<string>();
  const uniqueSuggestions: FilterSuggestion[] = [];

  for (const suggestion of sortedSuggestions) {
    const key = `${suggestion.filterId}:${suggestion.label}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueSuggestions.push(suggestion);
    }
  }

  // Limit to max suggestions
  return uniqueSuggestions.slice(0, opts.maxSuggestions);
}

/**
 * Highlight matching text in a suggestion label
 *
 * @param text - Full text to display
 * @param query - Search query to highlight
 * @returns Array of { text, highlighted } segments
 */
export function highlightMatch(
  text: string,
  query: string
): Array<{ text: string; highlighted: boolean }> {
  if (!query) {
    return [{ text, highlighted: false }];
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const segments: Array<{ text: string; highlighted: boolean }> = [];

  let lastIndex = 0;
  let searchIndex = 0;

  while (searchIndex < lowerText.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, searchIndex);

    if (matchIndex === -1) {
      // No more matches
      segments.push({
        text: text.slice(lastIndex),
        highlighted: false,
      });
      break;
    }

    // Add non-highlighted segment before match
    if (matchIndex > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, matchIndex),
        highlighted: false,
      });
    }

    // Add highlighted segment
    segments.push({
      text: text.slice(matchIndex, matchIndex + query.length),
      highlighted: true,
    });

    lastIndex = matchIndex + query.length;
    searchIndex = lastIndex;
  }

  return segments.length > 0 ? segments : [{ text, highlighted: false }];
}
